import { audToCents, centsToAudDisplay, centsToInput } from './money.js';
import { bpToInput, bpToPercentDisplay, percentToBp } from './percent.js';
import { canSeeCommissionConsole } from './capabilities.js';

/**
 * The pure core of the commission screen (Brief 6b): every string and number it
 * renders, and every validation verdict it reaches. Components wire these up;
 * they never build copy or parse input themselves — the buildFeeConfirm /
 * buildScholarshipConfirm precedent in lib/pricing.ts.
 *
 * THE RULE THAT RUNS THROUGH THIS FILE: branch on `kind` FIRST. The engine's
 * kind_value CHECK forces `basis` to 'whole_course' and nulls every other value
 * field on a 'none'/'inherit' row, so any helper that reads `basis` before
 * `kind` renders "Deal ended · whole course" — a fact about a column default,
 * not about the deal.
 */

export const COMMISSION_BASES = [
  'whole_course',
  'first_year',
  'first_6_months',
  'first_semester',
  'custom_months',
] as const;
export type CommissionBasis = (typeof COMMISSION_BASES)[number];
export type CommissionKind = 'terms' | 'none' | 'inherit';
export type CommissionDeduction = 'pre' | 'post';
export type CommissionPaymentRoute = 'via_aussizz' | 'direct';

/**
 * A commission-terms row as the web receives it (the overview's
 * `current`/`override`, or a history row). `kind`/`basis` are loose (string) so
 * any row-shaped object fits without casts — the ScholarshipLike precedent.
 */
export type CommissionTermsLike = {
  kind: string;
  rateBp: number | null;
  flatAmountCents: number | null;
  basis: string;
  basisMonths: number | null;
  deduction: string | null;
  paymentRoute: string | null;
  studentVisaForcesDirect: boolean;
  notes: string | null;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * A plain calendar date ('YYYY-MM-DD') → "1 Aug 2026". Same shape as
 * lib/format.ts's formatDate, WITHOUT its timezone trap: formatDate runs the
 * string through `new Date(...)`, which reads a bare date as UTC midnight, so a
 * viewer west of Greenwich renders the PREVIOUS day. `valid_from` and `asOf`
 * are calendar dates — no time, no zone — and must render identically
 * everywhere; the confirm-body tests must not depend on the runner's timezone.
 *
 * Use this for validFrom / asOf. Keep formatDate for createdAt, which is a real
 * instant where local-time rendering is correct.
 */
export function formatCommissionDate(date: string | null | undefined): string {
  if (!date) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return '—';
  const month = MONTHS[Number(m[2]) - 1];
  if (!month) return '—';
  return `${Number(m[3])} ${month} ${m[1]}`;
}

/** True only for a date that exists — rejects 2026-02-31 and friends. */
export function isRealCalendarDate(date: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return false;
  // UTC only — no local-timezone involvement, so this is stable everywhere.
  const probe = new Date(Date.UTC(y, mo - 1, d));
  return (
    probe.getUTCFullYear() === y && probe.getUTCMonth() === mo - 1 && probe.getUTCDate() === d
  );
}

/** Plain English for what a percentage applies to. Only meaningful for 'terms'. */
export function basisLabel(basis: string, basisMonths: number | null): string {
  switch (basis) {
    case 'whole_course':
      return 'whole course';
    case 'first_year':
      return 'first year';
    case 'first_6_months':
      return 'first 6 months';
    case 'first_semester':
      return 'first semester';
    case 'custom_months':
      // The CHECK guarantees 1–60 here; a null is a malformed row, not a
      // "custom period" — say so honestly rather than inventing a label.
      if (basisMonths == null) return '—';
      return basisMonths === 1 ? 'first month' : `first ${basisMonths} months`;
    default:
      return '—';
  }
}

/** null when no route was agreed — omit it rather than invent one. */
export function paymentRouteLabel(route: string | null): string | null {
  if (route === 'via_aussizz') return 'via Aussizz';
  if (route === 'direct') return 'direct';
  return null;
}

/** null when no deduction basis was agreed. */
export function deductionLabel(deduction: string | null): string | null {
  if (deduction === 'pre') return 'before deductions';
  if (deduction === 'post') return 'after deductions';
  return null;
}

/**
 * The money part of a deal: "20%" · "$500.00/student" · "20% + $250.00/student".
 *
 * `!= null`, never truthiness: a flat amount of 0 is a VALID deal server-side
 * (the API's flatAmountCents is nonnegative, unlike a scholarship's > 0), and
 * `if (v.flatAmountCents)` would silently erase it.
 *
 * Cents are shown in full ($500.00, not $500): the only whole-dollar formatter
 * rounds, and a $250.50 deal must never render as $251 on a commission screen.
 */
export function commissionValueLabel(v: {
  rateBp: number | null;
  flatAmountCents: number | null;
}): string {
  const parts: string[] = [];
  if (v.rateBp != null) parts.push(bpToPercentDisplay(v.rateBp));
  if (v.flatAmountCents != null) parts.push(`${centsToAudDisplay(v.flatAmountCents)}/student`);
  return parts.length > 0 ? parts.join(' + ') : '—';
}

/**
 * The one-line plain-English deal, for a college list row and the detail card.
 * "20% · whole course · via Aussizz" · "Deal ended" · "No deal set"
 */
export function commissionSummary(row: CommissionTermsLike | null | undefined): string {
  if (!row) return 'No deal set';
  if (row.kind === 'none') return 'Deal ended';
  if (row.kind === 'inherit') return 'Inherits college deal';
  if (row.kind !== 'terms') return 'No deal set';

  const value = commissionValueLabel(row);
  // A 'terms' row with neither value is malformed (the CHECK forbids it) —
  // report it honestly rather than papering over it.
  if (value === '—') return 'No deal set';

  const parts = [value, basisLabel(row.basis, row.basisMonths)];
  const route = paymentRouteLabel(row.paymentRoute);
  if (route) parts.push(route);
  return parts.filter((p) => p !== '—').join(' · ');
}

/** The fields commissionSummary doesn't carry. '' for any kind without a deal. */
export function commissionVersionDetail(v: CommissionTermsLike): string {
  if (v.kind !== 'terms') return '';
  const parts: string[] = [];
  const d = deductionLabel(v.deduction);
  if (d) parts.push(`Calculated ${d}`);
  if (v.studentVisaForcesDirect) parts.push('500-visa students: always paid direct');
  return parts.join(' · ');
}

/** History-row label — the sibling of scholarshipVersionLabel. */
export function commissionVersionLabel(v: CommissionTermsLike): string {
  if (v.kind === 'none') return 'Deal ended';
  if (v.kind === 'inherit') return 'Reverted to the college deal';
  return commissionSummary(v);
}

/**
 * A course row's state. Separate from commissionSummary because the WORDS
 * differ: no override means the college deal applies, not "no deal".
 */
export function courseOverrideSummary(
  override: CommissionTermsLike | null | undefined,
): string {
  if (!override) return 'College deal';
  if (override.kind === 'inherit') return 'Inherits college deal';
  if (override.kind === 'none') return 'Ended';
  return commissionSummary(override);
}

/**
 * Courses holding a current row that is SET and NOT 'inherit'. An 'inherit'
 * override is the college deal, so it is not a special deal; a 'none' override
 * IS one (this course deliberately pays nothing).
 */
export function overrideCount(
  courses: readonly { override: CommissionTermsLike | null }[],
): number {
  return courses.filter((c) => c.override !== null && c.override.kind !== 'inherit').length;
}

/** null when there are none, so the caller renders no badge at all. */
export function overrideCountLabel(count: number): string | null {
  if (count <= 0) return null;
  return count === 1 ? '1 special deal' : `${count} special deals`;
}

/** Live search for the college list. An empty query matches everything. */
export function matchesCollegeQuery(row: { providerName: string }, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (q === '') return true;
  return row.providerName.toLowerCase().includes(q);
}

/**
 * Who reaches the commission console. Brief 8b re-based this on CAPABILITIES —
 * it gates on `edit_commission`. See lib/capabilities.ts.
 *
 * Same verdicts as the role gate it replaces (admin + super_admin in; the other
 * four out), because edit_commission is exactly those two roles' preset. But it
 * now tracks the switch: grant edit_commission to a consultant and the console
 * appears, which is the bug this brick fixes.
 *
 * STILL FLAGGED, STILL NOT FIXED: `owner` and `accounts` hold view_commission
 * and the API's GET guards accept them — but this console is an unconditional
 * EDITOR, so admitting them would surface a form whose every Save 403s. A
 * read-only mode is the honest fix and needs its own brief; a gate on
 * view_commission alone would ship the broken screen instead.
 */
export const canViewCommissionConsole = canSeeCommissionConsole;

/**
 * POST bodies. Off-kind fields are OMITTED, not nulled: the API raises an
 * explicit validation issue for ANY deal field present on a 'none'/'inherit'
 * body, so sending `studentVisaForcesDirect: false` from stale form state is a
 * 400.
 *
 * `validFrom` is required here though the API leaves it optional: this screen
 * always sends the date the user is looking at. Omitting it would let the
 * server re-guess "today" — a save at 23:59 Sydney could land on tomorrow.
 */
export type CommissionTermsIntent = {
  kind: 'terms';
  rateBp?: number;
  flatAmountCents?: number;
  basis: CommissionBasis;
  basisMonths?: number;
  deduction?: CommissionDeduction;
  paymentRoute?: CommissionPaymentRoute;
  studentVisaForcesDirect: boolean;
  notes?: string;
  validFrom: string;
};
export type SetProviderCommissionBody =
  | CommissionTermsIntent
  | { kind: 'none'; validFrom: string; notes?: string };
export type SetCourseCommissionBody =
  | SetProviderCommissionBody
  | { kind: 'inherit'; validFrom: string; notes?: string };

/**
 * An intent → the row shape the label helpers read (undefined ≡ null). Mirrors
 * the server's normaliseDeal exactly, so a confirm dialog describes the row the
 * server will actually store.
 */
export function intentToRow(intent: SetCourseCommissionBody): CommissionTermsLike {
  if (intent.kind !== 'terms') {
    return {
      kind: intent.kind,
      rateBp: null,
      flatAmountCents: null,
      basis: 'whole_course',
      basisMonths: null,
      deduction: null,
      paymentRoute: null,
      studentVisaForcesDirect: false,
      notes: intent.notes ?? null,
    };
  }
  return {
    kind: 'terms',
    rateBp: intent.rateBp ?? null,
    flatAmountCents: intent.flatAmountCents ?? null,
    basis: intent.basis,
    basisMonths: intent.basisMonths ?? null,
    deduction: intent.deduction ?? null,
    paymentRoute: intent.paymentRoute ?? null,
    studentVisaForcesDirect: intent.studentVisaForcesDirect,
    notes: intent.notes ?? null,
  };
}

/** "…? Current: …" for a college-level save, and the brief's end-deal sentence. */
export function buildProviderCommissionConfirm(
  collegeName: string,
  next: SetProviderCommissionBody,
  current: CommissionTermsLike | null,
): string {
  if (next.kind === 'none') {
    return `End the deal with ${collegeName}? This college will show "Deal ended" from ${formatCommissionDate(next.validFrom)}.`;
  }
  return `Set the deal for ${collegeName} to ${commissionSummary(intentToRow(next))}, effective ${formatCommissionDate(next.validFrom)}? Current: ${commissionSummary(current)}`;
}

/** The course-scope twin, including retiring an override back to the college. */
export function buildCourseCommissionConfirm(
  courseName: string,
  next: SetCourseCommissionBody,
  current: CommissionTermsLike | null,
): string {
  if (next.kind === 'inherit') {
    return `Retire the special deal for ${courseName}? From ${formatCommissionDate(next.validFrom)} it follows the college deal again.`;
  }
  if (next.kind === 'none') {
    // Unreachable from this UI (there is no course-level End action), but the
    // endpoint admits the kind — an honest branch beats a throw.
    return `End commission for ${courseName}? This course will show "Ended" from ${formatCommissionDate(next.validFrom)}.`;
  }
  return `Set a special deal for ${courseName}: ${commissionSummary(intentToRow(next))}, effective ${formatCommissionDate(next.validFrom)}? Current: ${courseOverrideSummary(current)}`;
}

/** The editor's raw values — all strings, as typed. */
export type CommissionFormValues = {
  rate: string;
  flat: string;
  basis: CommissionBasis;
  basisMonths: string;
  deduction: '' | CommissionDeduction;
  paymentRoute: '' | CommissionPaymentRoute;
  studentVisaForcesDirect: boolean;
  notes: string;
  validFrom: string;
};

export type CommissionFieldErrors = {
  rate?: string;
  flat?: string;
  basisMonths?: string;
  notes?: string;
  validFrom?: string;
  form?: string;
};

/**
 * Prefill from the row in force. Branches on `kind` FIRST: a 'none'/'inherit'
 * row's basis is the CHECK's forced 'whole_course', not a choice anyone made,
 * so it must never seed the editor.
 */
export function initialCommissionForm(
  current: CommissionTermsLike | null | undefined,
  defaultValidFrom: string,
): CommissionFormValues {
  const blank: CommissionFormValues = {
    rate: '',
    flat: '',
    basis: 'whole_course',
    basisMonths: '',
    deduction: '',
    paymentRoute: '',
    studentVisaForcesDirect: false,
    notes: '',
    validFrom: defaultValidFrom,
  };
  if (!current || current.kind !== 'terms') return blank;
  return {
    rate: current.rateBp != null ? bpToInput(current.rateBp) : '',
    // `!= null`, not truthy — a stored $0 flat must round-trip into the editor,
    // or the deal could never be edited again.
    flat: current.flatAmountCents != null ? centsToInput(current.flatAmountCents) : '',
    basis: (COMMISSION_BASES as readonly string[]).includes(current.basis)
      ? (current.basis as CommissionBasis)
      : 'whole_course',
    basisMonths: current.basisMonths != null ? String(current.basisMonths) : '',
    deduction: current.deduction === 'pre' || current.deduction === 'post' ? current.deduction : '',
    paymentRoute:
      current.paymentRoute === 'via_aussizz' || current.paymentRoute === 'direct'
        ? current.paymentRoute
        : '',
    studentVisaForcesDirect: current.studentVisaForcesDirect,
    notes: current.notes ?? '',
    // Never the old version's date — this save creates a NEW version.
    validFrom: defaultValidFrom,
  };
}

/**
 * Parse + validate the editor into a POST body, mirroring the API's schema
 * field for field so bad input dies here rather than as a 400 (and never at the
 * DB CHECK).
 */
export function parseCommissionForm(
  v: CommissionFormValues,
): { ok: true; body: CommissionTermsIntent } | { ok: false; errors: CommissionFieldErrors } {
  const errors: CommissionFieldErrors = {};

  let rateBp: number | undefined;
  if (v.rate.trim() !== '') {
    // percentToBp's domain (1–10000 bp, max 2 decimals) is the server's rate_bp
    // CHECK exactly — reuse it rather than restate the rule.
    const bp = percentToBp(v.rate);
    if (bp === null) errors.rate = 'Enter a percent from 0.01 to 100, max 2 decimals.';
    else rateBp = bp;
  }

  let flatAmountCents: number | undefined;
  if (v.flat.trim() !== '') {
    // `=== null`, not falsy: 0 is a valid flat amount.
    const cents = audToCents(v.flat);
    if (cents === null) errors.flat = 'Enter an amount — numbers only, max 2 decimals.';
    else flatAmountCents = cents;
  }

  // At least one value is required; both together are allowed. `undefined`, not
  // falsy — a $0 flat counts as a value.
  if (rateBp === undefined && flatAmountCents === undefined && !errors.rate && !errors.flat) {
    errors.form = 'Enter a rate, a flat amount, or both.';
  }

  let basisMonths: number | undefined;
  if (v.basis === 'custom_months') {
    const raw = v.basisMonths.trim();
    const n = /^\d{1,2}$/.test(raw) ? Number(raw) : NaN;
    if (!Number.isInteger(n) || n < 1 || n > 60) {
      errors.basisMonths = 'Enter a whole number of months from 1 to 60.';
    } else {
      basisMonths = n;
    }
  }
  // For any other basis basisMonths is DROPPED, never sent: the API rejects the
  // extra field with a 400.

  const notes = v.notes.trim();
  if (notes.length > 2000) errors.notes = 'Notes cannot exceed 2000 characters.';

  if (!isRealCalendarDate(v.validFrom)) {
    errors.validFrom = 'Enter a date as YYYY-MM-DD.';
  }
  // No lower bound: the API permits any date and the brief asks for
  // future-dating, so inventing a stricter client rule would diverge.

  if (Object.keys(errors).length > 0) return { ok: false, errors };

  return {
    ok: true,
    body: {
      kind: 'terms',
      ...(rateBp !== undefined ? { rateBp } : {}),
      ...(flatAmountCents !== undefined ? { flatAmountCents } : {}),
      basis: v.basis,
      ...(basisMonths !== undefined ? { basisMonths } : {}),
      ...(v.deduction !== '' ? { deduction: v.deduction } : {}),
      ...(v.paymentRoute !== '' ? { paymentRoute: v.paymentRoute } : {}),
      studentVisaForcesDirect: v.studentVisaForcesDirect,
      // Blank → omitted → the server stores NULL, not an empty string.
      ...(notes !== '' ? { notes } : {}),
      validFrom: v.validFrom,
    },
  };
}
